import { useMutation } from '@apollo/client';
import { useCallback, useEffect } from 'react';

import type {
  DeleteLineItemCustomPacingV5Mutation,
  DeleteLineItemCustomPacingV5MutationVariables,
} from '../../apis/graphql';
import { openErrorToastAlert } from '../../common/ToastAlert/toastAlert';
import { DELETE_LINE_ITEM_CUSTOM_PACING_BY_IDS } from './deleteLineItemCustomPacingV5';
import type { DeleteCustomPacingError } from './utils';
import {
  handleDeleteCustomPacingCacheUpdate,
  handleDeleteCustomPacingErrors,
  showSuccessDeleteCustomPacingMessage,
} from './utils';

type UseDeleteCustomPacing = {
  handleDelete: (lineItemIds: string[]) => Promise<void>;
  loading: boolean;
};

export const useDeleteCustomPacings = (): UseDeleteCustomPacing => {
  const [deleteLineItemCustomPacings, { error, loading }] = useMutation<
    DeleteLineItemCustomPacingV5Mutation,
    DeleteLineItemCustomPacingV5MutationVariables
  >(DELETE_LINE_ITEM_CUSTOM_PACING_BY_IDS, {});

  useEffect(() => {
    if (error) {
      openErrorToastAlert(error.name, error.message);
    }
  }, [error]);

  const handleDelete = useCallback(
    async (lineItemIds: string[]) => {
      if (!lineItemIds.length) return;

      await deleteLineItemCustomPacings({
        variables: {
          lineItemIds,
        },
        update(cache, { data }) {
          if (data) {
            const errors: DeleteCustomPacingError[] = [];
            let updatedLineItemsAmount: number = 0;

            data.deleteLineItemCustomPacingV5.forEach((customPacingResponse) => {
              if (!customPacingResponse.success) {
                errors.push({
                  id: customPacingResponse.lineItemId,
                  message: customPacingResponse.message,
                  errorCode: customPacingResponse.errorCode,
                });

                return;
              }

              handleDeleteCustomPacingCacheUpdate(cache, customPacingResponse);
              updatedLineItemsAmount++;
            });

            if (errors.length) {
              handleDeleteCustomPacingErrors(errors);
            } else {
              showSuccessDeleteCustomPacingMessage(updatedLineItemsAmount);
            }
          }
        },
      });
    },
    [deleteLineItemCustomPacings]
  );

  return {
    handleDelete,
    loading,
  };
};
